import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  axiosInstance,
  UNAUTHORIZED_STATUS_CODE,
} from 'src/constants/axiosInstance';
import { API_ROUTES_FOR_AXIOS } from 'src/constants/ApiRoutes';
import { PATHS, SEARCH_PARAMS } from 'src/constants/navigateRoutes';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import { getNavigatePathWithRedirectUrl } from 'src/utils/commonValidation';
import { isBackendAxiosRequest } from 'src/utils/axios';
import UpgradePopup from 'src/components/upgrade-popup/UpgradePopup';
import { FREE_PLAN_INVOCATION_LIMIT_ERROR_CODE } from 'src/constants/invocationUpsell';
import { UpgradeOffer } from 'src/types/upgradeOffer';
import {
  DASHBOARD_UNAUTHORIZED_REASON_CODES,
  DashboardUnauthorizedReasonCode,
} from 'src/constants/dashboardAuth';

const normalizeRequestPath = (url: string) =>
  url.split('?')[0].replace(/^\/+/, '');

const ONBOARDING_SAFE_401_ENDPOINTS = [
  API_ROUTES_FOR_AXIOS.getSupportedLanguages(),
  API_ROUTES_FOR_AXIOS.getSupportedTimezones(),
  API_ROUTES_FOR_AXIOS.checkDnsVerification(),
  API_ROUTES_FOR_AXIOS.getOrganizationsForDomainDirectory(),
  API_ROUTES_FOR_AXIOS.lookupOrganizationsForDomainDirectoryByEmail(),
  API_ROUTES_FOR_AXIOS.getSignUpUrl(),
  API_ROUTES_FOR_AXIOS.getMaintenanceStatus(),
  API_ROUTES_FOR_AXIOS.getSessionDetails(),
].map((endpoint) => normalizeRequestPath(endpoint));

type ShouldIgnoreUnauthorizedParams = {
  pathname: string;
  requestUrl: string;
  skipUnauthorizedLogout?: boolean;
};

export const shouldIgnoreUnauthorizedLogout = ({
  pathname,
  requestUrl,
  skipUnauthorizedLogout,
}: ShouldIgnoreUnauthorizedParams) => {
  if (skipUnauthorizedLogout) {
    return true;
  }

  if (
    pathname !== PATHS.onBoardRegistration &&
    pathname !== PATHS.unAuthorized
  ) {
    return false;
  }

  const normalizedRequestPath = normalizeRequestPath(requestUrl);

  return ONBOARDING_SAFE_401_ENDPOINTS.some(
    (safePath) =>
      normalizedRequestPath === safePath ||
      normalizedRequestPath.startsWith(`${safePath}/`),
  );
};

const NO_ACTIVE_ORG_SAFE_PATHS = new Set([
  PATHS.onBoardRegistration,
  PATHS.noActiveOrganization,
]);

const KNOWN_UNAUTHORIZED_REASONS = new Set<DashboardUnauthorizedReasonCode>(
  Object.values(DASHBOARD_UNAUTHORIZED_REASON_CODES),
);

const AxiosInterceptor = observer(({ children }: PropsWithChildren) => {
  const { logout, userDetails } = useAuthStore();
  const navigate = useNavigate();
  const isHandlingUnauthorizedRef = useRef(false);
  const [limitUpgradePopupOpen, setLimitUpgradePopupOpen] = useState(false);
  const [limitUpgradeOffer, setLimitUpgradeOffer] = useState<UpgradeOffer>();

  useEffect(() => {
    if (userDetails) {
      isHandlingUnauthorizedRef.current = false;
    }
  }, [userDetails]);

  useEffect(() => {
    const shouldIgnoreNoActiveOrganizationUnauthorized = () =>
      NO_ACTIVE_ORG_SAFE_PATHS.has(window.location.pathname);

    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const status = error.response?.status;
        const requestUrl =
          typeof error.config?.url === 'string' ? error.config.url : '';
        const baseUrl =
          typeof error.config?.baseURL === 'string' ? error.config.baseURL : '';
        const skipUnauthorizedLogout = Boolean(
          error.config?.skipUnauthorizedLogout,
        );
        const data = error.response?.data as {
          errorCode?: string | number;
          upgradeOffer?: UpgradeOffer;
        };
        const errorCode = data?.errorCode;
        const isNoActiveOrganizationError =
          errorCode === DASHBOARD_UNAUTHORIZED_REASON_CODES.ORG_NOT_FOUND ||
          errorCode ===
            DASHBOARD_UNAUTHORIZED_REASON_CODES.MEMBERSHIP_LOOKUP_FAILED;
        const unauthorizedReason =
          typeof errorCode === 'string' &&
          KNOWN_UNAUTHORIZED_REASONS.has(
            errorCode as DashboardUnauthorizedReasonCode,
          )
            ? (errorCode as DashboardUnauthorizedReasonCode)
            : null;

        if (
          status === UNAUTHORIZED_STATUS_CODE &&
          requestUrl !== API_ROUTES_FOR_AXIOS.logout() &&
          !isHandlingUnauthorizedRef.current &&
          !shouldIgnoreUnauthorizedLogout({
            pathname: window.location.pathname,
            requestUrl,
            skipUnauthorizedLogout,
          }) &&
          isBackendAxiosRequest({ requestUrl, baseUrl })
        ) {
          if (
            isNoActiveOrganizationError &&
            shouldIgnoreNoActiveOrganizationUnauthorized()
          ) {
            return Promise.reject(error);
          }

          isHandlingUnauthorizedRef.current = true;

          if (isNoActiveOrganizationError) {
            navigate(PATHS.noActiveOrganization);

            return Promise.reject(error);
          }

          const userAccessedUrl = window.location.href;
          const encodedUserAccessedUrl = encodeURIComponent(userAccessedUrl);
          const redirectPath = getNavigatePathWithRedirectUrl(
            PATHS.unAuthorized,
            window.location.pathname,
            encodedUserAccessedUrl,
            {
              [SEARCH_PARAMS.reason]: unauthorizedReason,
            },
          );

          await logout();
          navigate(redirectPath);
        }

        if (
          isBackendAxiosRequest({ requestUrl, baseUrl }) &&
          status === 402 &&
          data?.errorCode === FREE_PLAN_INVOCATION_LIMIT_ERROR_CODE &&
          data?.upgradeOffer
        ) {
          setLimitUpgradeOffer(data.upgradeOffer);
          setLimitUpgradePopupOpen(true);
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axiosInstance.interceptors.response.eject(interceptor);
    };
  }, [logout, navigate]);

  return (
    <>
      {children}
      <UpgradePopup
        isOpen={limitUpgradePopupOpen}
        setIsOpen={setLimitUpgradePopupOpen}
        upgradeOffer={limitUpgradeOffer}
      />
    </>
  );
});

export default AxiosInterceptor;
